import React, { useState, useContext, useRef } from "react";
import { Image, Button, Dropdown, Spinner, OverlayTrigger, Tooltip, Modal, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShareFromSquare,
  faDownload,
  faEdit,
  faTrash,
  faCog,
  faCamera,
  faMapMarkerAlt,
  faHeart as faHeartSolid,
  faMusic,
  faBuilding,
  faVideo,
  faIdBadge,
  faUsers,
  faClone,
  faCalendar,
  faLanguage,
  faImage
} from "@fortawesome/free-solid-svg-icons";
import { faHeart as faHeartRegular } from "@fortawesome/free-regular-svg-icons";
import { useNavigate } from "react-router-dom";
import { APIGetDownloadableVersion, APIEditVisualizations, APIWebVisualization, APICreateMtlCopy } from "../helper/APIFunctions";
import { downloadHtml, buildFolderTree, isMobile } from "../helper/Utils";
import { snorkleValidator } from "../helper/WebResourceValidators";
import ShareModal from "./VisualModals/Share";
import DeleteModal from "./VisualModals/Delete";
import EditModal from "./VisualModals/Edit";
import InitialViewModal from "./VisualModals/ChangeView";
import LandmarkModal from "./VisualModals/Landmarks";
import LandmarksMessagingModal from "./VisualModals/LandmarksMessagingModal";
import AudioModal from "./VisualModals/AudioModal";
import SnapshotModal from "./VisualModals/Snapshot";
import DoiRequestModal from "./DoiRequestModal";
import './CarouselVisCard.css';
import defaultThumbnail from '../assets/atom_science_logo.png';
import { UserInfo } from "../helper/Context";
import { canRequestDOI, getVisualLifetimeHrs } from "../helper/BusinessLogic";
import { getColorForFolder } from "../helper/Utils";
import { CLOUD_TRANSLATE_LANGUAGE_MAP, languageSortFunction, ANATOMY_EXPLORER_LANGUAGES } from "../helper/Language_Utils";
import VoiceSelector from "./VoiceSelector";

const RenderFolderItems = ({ folders, onSelect, selectedFolder, level = 0 }) => {
  const prefix = "\u00A0\u00A0\u00A0\u00A0".repeat(level);
  return folders.map(folder => (
    <React.Fragment key={folder.id}>
      <Dropdown.Item onClick={() => onSelect(folder.id)}>
        {prefix}{folder.title}
      </Dropdown.Item>
      {folder.children && folder.children.length > 0 && (
        <RenderFolderItems folders={folder.children} onSelect={onSelect} selectedFolder={selectedFolder} level={level + 1} />
      )}
    </React.Fragment>
  ));
};

const langOptions = Object.keys(CLOUD_TRANSLATE_LANGUAGE_MAP).map((lang) => ({
  value: lang,
  label: CLOUD_TRANSLATE_LANGUAGE_MAP[lang],
})).sort((a,b) => languageSortFunction(a.value,b.value));

const CarouselVisCard = ({ vis, onVisDelete, onVisEdit, isOrg, folders = [], blockVisLink = false, blockActions = false, folderTree = [], lockoutEdit = false, lockoutDelete = false }) => {
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showInitialViewModal, setShowInitialViewModal] = useState(false);
  const [showLandmarkModal, setShowLandmarkModal] = useState(false);
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [showSnapshotModal, setShowSnapshotModal] = useState(false);
  const [showDoiModal, setShowDoiModal] = useState(false);
  const [showMobileWarning, setShowMobileWarning] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  const [isFavorite, setIsFavorite] = useState(vis.favorite);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [language, setLanguage] = useState("");
  const [showMtlModal, setShowMtlModal] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState({ state: null });
  const [mtlCopyStatus, setMtlCopyStatus] = useState({ state: null });
  const [landmarkAudioAction, setLandmarkAudioAction] = useState("keep"); // keep, delete, dub
  const [dubbingVoice, setDubbingVoice] = useState("");
  const navigate = useNavigate();
  const { userInfo } = useContext(UserInfo);
  const canEdit = (userInfo?.organization_permissions?.edit_visuals || !isOrg) && !lockoutEdit && !blockActions;
  const canDelete = (userInfo?.organization_permissions?.delete_visuals || !isOrg) && !lockoutDelete && !blockActions;


  const handleToggleFavorite = () => {
    if (!canEdit) return;
    const newFavoriteStatus = !isFavorite;
    setIsFavorite(newFavoriteStatus);
    APIEditVisualizations(
      vis, null, null, null, null,
      () => {
        onVisEdit({ ...vis, favorite: newFavoriteStatus });
      },
      (err) => {
        setIsFavorite(!newFavoriteStatus); // Revert on error
      },
      isOrg, { favorite: newFavoriteStatus }
    );
  };

  const handleMoveFolder = (newFolderId) => {
    APIEditVisualizations(
      vis, null, null, null, null,
      () => {
        onVisEdit({ ...vis, folder_id: newFolderId });
      },
      isOrg, { folder_id: newFolderId }
    );
  };

  const handleClone = (toOrg = false) => {
    const visualId = snorkleValidator(vis.visualization_url);
    if (!visualId) {
      alert('Could not extract a valid ID from the visual URL.');
      return;
    }

    setIsCloning(true);
    APIWebVisualization(
      visualId,
      'snorkle',
      vis.title,
      vis.description,
      (response) => {
        setIsCloning(false);
        alert(`Visual cloned successfully! It will now appear in your ${toOrg ? 'team' : 'personal'} library.`);
        window.location.reload();
      },
      (error) => {
        setIsCloning(false);
        alert(`Failed to clone visual: ${error.message || 'An unknown error occurred.'}`);
      },
      toOrg
    );
  };

  const handleCreateMtlCopy = () => {
    APICreateMtlCopy(
      vis.id,
      language,
      false,//isOrg,
      landmarkAudioAction,
      dubbingVoice,
      () => {
        setMtlCopyStatus({ state: "success" });
        setTimeout(() => {
          setShowMtlModal(false);
          window.location.reload();
        }, 1000);
      },
      (err) => {
        setMtlCopyStatus({ state: "error", msg: (err.message || (typeof err === 'string' ? err : 'An unknown error occurred.')).replaceAll('"', "") });
      },
      `Copying "${vis.title}" to ${CLOUD_TRANSLATE_LANGUAGE_MAP[language]}...`,
    );
    setMtlCopyStatus({ state: "loading" });
  };

  const handlePreview = (e) => {
    e.preventDefault();
    window.open(vis.visualization_url, "_blank");
  };

  const handleDownload = () => {
    setDownloadStatus({ state: "loading" });
    APIGetDownloadableVersion(
      vis.id,
      (body) => {
        downloadHtml(body.html, `${vis.title}.html`);
        setDownloadStatus({ state: "success" });
      },
      (err) => {
        setDownloadStatus({ state: "error", msg: "Could not get download." });
      },
      isOrg
    );
  };

  const visViewerUrl = vis.visualization_url;
  const folderName = folders.find(f => f.id === vis.folder_id)?.title;
  const folderColor = getColorForFolder(vis.folder_id, folderName);

  let expirationDate = null;
  if (vis.expire_timestamp) {
    expirationDate = new Date(vis.expire_timestamp * 1000);
  }

  return (
    <>
      <div className={`carousel-vis-card ${isDropdownOpen && !blockActions ? 'dropdown-open' : ''}`}>
        {(isOrg || vis.isOrgVis) && (
          <div className="team-visual-indicator" title="Team Visual">
            <FontAwesomeIcon icon={faUsers} />
          </div>
        )}
        <a
          href={blockVisLink ? null : vis.visualization_url}
          onClick={blockVisLink ? (e) => e.preventDefault() : handlePreview}
          className="carousel-vis-card-thumbnail-container"
          style={{ cursor: blockVisLink ? 'default' : 'pointer' }}
        >
          {vis.type === "video" && (
            <span className="primary-card-overlay-icon" title="Video">
              <FontAwesomeIcon icon={faVideo} />
            </span>
          )}
          <Image src={vis.thumbnail || defaultThumbnail} className={`carousel-vis-card-thumbnail ${!vis.thumbnail ? 'no-thumbnail' : ''}`} />
          {vis.description ? <div className="carousel-vis-card-description">{vis.description}</div> : null}
        </a>
        <div className="carousel-vis-card-body">
          <div className="d-flex justify-content-between align-items-start">
            <OverlayTrigger
              placement="top"
              delay={{ show: 1500, hide: 250 }}
              overlay={<Tooltip id={`tooltip-title-${vis.id}`}>{vis.title}</Tooltip>}
            >
              <h5 className="carousel-vis-card-title">{vis.title}</h5>
            </OverlayTrigger>
            <OverlayTrigger
              placement="top"
              delay={{ show: 1500, hide: 250 }}
              overlay={<Tooltip id={`tooltip-favorite-${vis.id}`}>{isFavorite ? 'Remove from favorites' : 'Add to favorites'}</Tooltip>}
            >
              <Button onClick={blockActions ? (e) => e.preventDefault() : handleToggleFavorite} disabled={!canEdit}
                className="carousel-card-icon-button favorite-overlay-button"
                style={{
                  cursor: !canEdit ? 'default' : 'pointer',
                  pointerEvents: !canEdit ? 'none' : 'auto'
                }}>
                <FontAwesomeIcon icon={isFavorite ? faHeartSolid : faHeartRegular} />
              </Button>
            </OverlayTrigger>
          </div>

          <div className="carousel-vis-card-meta mb-2">{(!canEdit) ?
            (<div className="vis-card-folder-label" style={{ borderLeft: `5px solid ${folderColor}`, pointerEvents: 'none' }}>
              {folderName || 'Uncategorized'}
            </div>)
            : (
              <Dropdown onToggle={blockActions ? () => { } : setIsDropdownOpen}>
                <Dropdown.Toggle as="div" className="vis-card-folder-label" style={{ borderLeft: `5px solid ${folderColor}` }}>
                  {folderName || 'Uncategorized'}
                </Dropdown.Toggle>
                <Dropdown.Menu align="end" flip={true} style={{ zIndex: 1050 }}>
                  <Dropdown.Item onClick={() => blockActions ? {} : handleMoveFolder(null)} disabled={!vis.folder_id}>
                    Uncategorized
                  </Dropdown.Item>
                  <RenderFolderItems folders={folderTree || buildFolderTree(folders)} onSelect={handleMoveFolder} selectedFolder={vis.folder_id} />
                </Dropdown.Menu>
              </Dropdown>
            )}
            {expirationDate && (
              <div className="vis-card-expiration" style={{ color: '#a0c4ff' }}>
                <FontAwesomeIcon icon={faCalendar} className="me-1" />
                Expires: {expirationDate.toLocaleDateString()}
              </div>
            )}
            {/* <div className="vis-card-tags">
              {(vis.tags || []).map(tag => <span key={tag.slug} className="vis-card-tag">#{tag.name}</span>)}
            </div> */}
          </div>
          {!blockActions && (
            <div className="carousel-vis-card-actions">
              <OverlayTrigger
                placement="top"
                delay={{ show: 1500, hide: 250 }}
                overlay={<Tooltip id={`tooltip-share-${vis.id}`}>Share</Tooltip>}
              >
                <span
                  role="button"
                  tabIndex="0"
                  onClick={() => setShowShareModal(true)}
                  onKeyDown={(e) => e.key === 'Enter' && setShowShareModal(true)}
                  className="card-action-icon"
                >
                  <FontAwesomeIcon icon={faShareFromSquare} />
                </span>
              </OverlayTrigger>
              <Dropdown onToggle={setIsDropdownOpen}>
                <OverlayTrigger
                  placement="top"
                  delay={{ show: 1500, hide: 250 }}
                  overlay={<Tooltip id={`tooltip-settings-${vis.id}`}>Settings</Tooltip>}
                >
                  <Dropdown.Toggle as="span" className="card-action-icon" id="dropdown-basic">
                    <FontAwesomeIcon icon={faCog} />
                  </Dropdown.Toggle>
                </OverlayTrigger>
                <Dropdown.Menu align="end" flip={true} style={{ zIndex: 1050 }}>
                  <Dropdown.Item onClick={() => setShowEditModal(true)} disabled={!canEdit}>
                    <FontAwesomeIcon icon={faEdit} className="me-2" /> Settings
                  </Dropdown.Item>
                  {(isOrg || vis.isOrgVis) && (
                    <Dropdown.Item onClick={handleClone} disabled={isCloning}>
                      <FontAwesomeIcon icon={faClone} className="me-2" />
                      {isCloning ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" className="me-2" />
                          Cloning...
                        </>
                      ) : (
                        "Clone a copy"
                      )}
                    </Dropdown.Item>
                  )}
                  {canRequestDOI(userInfo) && !vis.doi && (
                    <Dropdown.Item onClick={() => setShowDoiModal(true)}>
                      <FontAwesomeIcon icon={faIdBadge} className="me-2" /> Request DOI
                    </Dropdown.Item>
                  )}
                  {vis.can_save_view && (
                    <Dropdown.Item onClick={() => setShowInitialViewModal(true)} disabled={!canEdit}>
                      <FontAwesomeIcon icon={faCamera} className="me-2" /> Change Initial View
                    </Dropdown.Item>
                  )}
                  {vis.can_snapshot && (
                    <Dropdown.Item onClick={() => setShowSnapshotModal(true)} disabled={!canEdit}>
                      <FontAwesomeIcon icon={faImage} className="me-2" /> Change Thumbnail
                    </Dropdown.Item>
                  )}
                  {vis.can_landmark && (
                    <Dropdown.Item
                      onClick={() => {
                        if (isMobile()) {
                          setShowMobileWarning(true);
                        } else {
                          setShowLandmarkModal(true);
                        }
                      }}
                      disabled={!canEdit}
                    >
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" /> Add/Change Landmarks
                    </Dropdown.Item>
                  )}
                  {
                    userInfo?.permissions?.mtl && !vis.type.startsWith("image-seadragon") && (
                      <Dropdown.Item onClick={() => setShowMtlModal(true)} disabled={!canEdit}>
                        <FontAwesomeIcon icon={faLanguage} className="me-2" /> Create Translated Copy
                      </Dropdown.Item>
                    )
                  }
                  <Dropdown.Item onClick={() => setShowAudioModal(true)} disabled={!canEdit}>
                    <FontAwesomeIcon icon={faMusic} className="me-2" /> Add/Replace Audio
                  </Dropdown.Item>
                  {userInfo && userInfo?.organization_permissions && userInfo?.organization_permissions?.add_visuals && !isOrg && (
                    <Dropdown.Item onClick={() => handleClone(true)} disabled={isCloning}>
                      <FontAwesomeIcon icon={faBuilding} className="me-2" /> Add to Org Portfolio
                    </Dropdown.Item>
                  )}
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={() => setShowDeleteModal(true)} className="text-danger" disabled={!canDelete}>
                    <FontAwesomeIcon icon={faTrash} className="me-2" /> Delete
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          )}
        </div>
      </div>

      <Modal show={showMobileWarning} onHide={() => setShowMobileWarning(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Desktop Recommended</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>This feature is best used on Desktop. Mobile support is limited.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => { setShowMobileWarning(false); setShowLandmarkModal(true); }}>
            Understood
          </Button>
        </Modal.Footer>
      </Modal>

      {showShareModal && <ShareModal
        show={showShareModal}
        onHide={() => setShowShareModal(false)}
        url={visViewerUrl}
        qr={vis.qr_image || vis.qr_url}
        id={vis.id}
        canEmail={vis.can_email}
        type={vis?.type}
        subtype={vis?.subtype}
      />}
      {canDelete && showDeleteModal && <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        vis={vis}
        onVisDelete={() => { onVisDelete(vis.id); setShowDeleteModal(false); }}
        isOrg={isOrg}
      />}
      {canEdit && showEditModal && <EditModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        vis={vis}
        onVisEdit={(vis) => { onVisEdit(vis); setShowEditModal(false); }}
        isOrg={isOrg}
      />}
      {canEdit && showInitialViewModal && <InitialViewModal
        show={showInitialViewModal}
        onHide={() => setShowInitialViewModal(false)}
        vis={vis}
        isOrg={isOrg}
      />}
      {canEdit && showLandmarkModal && (vis.engine == "sg" || vis?.type?.includes("scenegraph") ? <LandmarksMessagingModal
        show={showLandmarkModal}
        onHide={() => setShowLandmarkModal(false)}
        vis={vis}
        isOrg={isOrg}
      /> : <LandmarkModal
        show={showLandmarkModal}
        onHide={() => setShowLandmarkModal(false)}
        vis={vis}
        isOrg={isOrg}
      />)}
      {canEdit && showAudioModal && (
        <AudioModal
          show={showAudioModal}
          onHide={() => setShowAudioModal(false)}
          vis={vis}
          isOrg={isOrg}
          onVisEdit={onVisEdit}
        />
      )}
      {showDoiModal && (
        <DoiRequestModal
          show={showDoiModal}
          onHide={() => setShowDoiModal(false)}
          visualUrl={vis.visualization_url}
        />
      )}
      {userInfo?.permissions?.mtl && showMtlModal  && (
        <Modal
          show={showMtlModal}
          onHide={() => setShowMtlModal(false)}
        >
          <Modal.Header closeButton closeVariant="white">Create Translated Copy</Modal.Header>
          <Modal.Body>
            {mtlCopyStatus.state === "error" && <div className="text-danger">{mtlCopyStatus.msg}</div>}
            <Form.Group controlId="formLanguage">
              <Form.Label>Language</Form.Label>
              <Form.Control as="select" value={language} onChange={(e) => setLanguage(e.target.value)} disabled={mtlCopyStatus.state === "loading"} className="mb-3">
                <option value="">Select a language</option>
                {langOptions.filter((lang) => vis?.subtype === "anatomy_scene" ? ANATOMY_EXPLORER_LANGUAGES.includes(lang.value) : true).map((lang) => (
                  <option key={lang.value} value={lang.value}>{lang.label}</option>
                ))}
              </Form.Control>
            </Form.Group>
            {vis.can_landmark && vis.can_audio_landmark && (
              <>
              <Form.Group controlId="formDeleteLandmarkAudio">
                <Form.Label>Landmark Audio</Form.Label>
                <Form.Select value={landmarkAudioAction} onChange={(e) => setLandmarkAudioAction(e.target.value)} disabled={mtlCopyStatus.state === "loading"}>
                  <option value="keep">Keep original audio</option>
                  <option value="delete">Remove audio</option>
                  {userInfo?.permissions?.landmark_dubbing && <option value="dub">Replace with new audio</option>}
                </Form.Select>
              </Form.Group>
              {userInfo?.permissions?.landmark_dubbing && landmarkAudioAction === "dub" && (
                <VoiceSelector voice={dubbingVoice} onVoiceChange={(voice) => setDubbingVoice(voice)} disabled={mtlCopyStatus.state === "loading"} language={language} selectClassName="form-select" playbackRate={1.0} />
              )}
              </>
            )}
            <Button variant="primary" onClick={() => handleCreateMtlCopy()} disabled={!language || mtlCopyStatus.state === "loading"}>{mtlCopyStatus.state === "loading" ? (<>Creating... <Spinner animation="border" size="sm" /></>) : "Create Translated Copy"}</Button>
          </Modal.Body>
        </Modal>
      )}
      {showSnapshotModal && (
        <SnapshotModal
          vis={vis}
          url={vis.visualization_url}
          onClose={() => setShowSnapshotModal(false)}
          isOrg={isOrg}
          filebase={vis.title.replace(/ /g, "_")}
          onNewThumb={(newThumb) => {
            setShowSnapshotModal(false);
            onVisEdit({
              ...vis,
              thumbnail: newThumb
            });
          }}
          canEdit={true}
        />
      )}
    </>
  );
};

export default CarouselVisCard; 